import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import clsx from 'clsx';
import StatusTag from 'core/components/StatusTag';
import ToolTip from 'core/components/Tooltip';
import type {
  PatientConsentStatus,
} from 'dtos/patientConsentStatus';
import {
  AppointmentIntakeSectionStatus,
  AppointmentIntakeStatus,
} from 'pages/Dashboard/types/appointmentIntakeStatus';
import {
  getAppointmentIntakeSummary,
} from 'pages/Dashboard/utils/helper';

type ConsentDisplayStatus = PatientConsentStatus | 'NotAvailable';

type Props = {
  intakeStatus: AppointmentIntakeStatus;
};

type ConsentProps = {
  consentStatus: ConsentDisplayStatus;
};

const intakeStatusLabels: Record<AppointmentIntakeSectionStatus, string> = {
  Completed: 'Completed',
  Incomplete: 'Incomplete',
  NotSent: 'Not Sent',
};

const consentStatusLabels: Record<ConsentDisplayStatus, string> = {
  Given: 'Signed',
  Pending: 'Not Signed',
  NotRequired: 'Not Required',
  NotAvailable: 'Not Available',
};

type StatusLineProps = {
  label: string;
  status: AppointmentIntakeSectionStatus;
};

function StatusLine({
  label,
  status,
}: StatusLineProps) {
  return (
    <div className="flex min-w-0 items-center gap-1.5 border-l border-gray-300 px-3 first:border-l-0 first:pl-0 last:pr-0">
      <ToolTip title={intakeStatusLabels[status]}>
        <span className="inline-flex shrink-0">
          {status === 'Completed' && (
            <CheckCircleIcon
              aria-label={`${label}: ${intakeStatusLabels[status]}`}
              className="!h-4 !w-4 text-emerald-700"
            />
          )}
          {status === 'Incomplete' && (
            <InfoOutlinedIcon
              aria-label={`${label}: ${intakeStatusLabels[status]}`}
              className="!h-4 !w-4 text-red-600"
            />
          )}
          {status === 'NotSent' && (
            <RemoveIcon
              aria-label={`${label}: ${intakeStatusLabels[status]}`}
              className="!h-4 !w-4 text-gray-400"
            />
          )}
        </span>
      </ToolTip>
      <span className={clsx({
        'text-gray-400': status === 'NotSent',
        'text-gray-800': status !== 'NotSent',
      })}
      >
        {label}
      </span>
    </div>
  );
}

export function AppointmentConsentStatusRow({
  consentStatus,
}: ConsentProps) {
  return (
    <StatusTag
      value={consentStatusLabels[consentStatus]}
      statusMap={{
        success: 'Signed',
        error: 'Not Signed',
        off: consentStatus === 'NotRequired' ? 'Not Required' : 'Not Available',
      }}
      classes={{
        root: 'shrink-0 justify-center',
        value: 'gap-1',
      }}
    />
  );
}

export default function AppointmentPreVisitStatusRows({
  intakeStatus,
}: Props) {
  const {
    requestedCount,
    completedCount,
    summaryStatus,
  } = getAppointmentIntakeSummary(intakeStatus);
  const progressLabel = `${completedCount} Of ${requestedCount} Completed`;
  let summaryLabel = summaryStatus;
  if (requestedCount > 0 && summaryStatus !== 'Completed') {
    summaryLabel = progressLabel;
  } else if (summaryStatus === 'Reminder only') {
    summaryLabel = 'Reminder Only';
  } else if (summaryStatus === 'Not sent') {
    summaryLabel = 'Not Sent';
  }

  return (
    <div className="px-2 py-3 text-xs">
      <div className="flex items-center justify-between gap-3">
        <span className="font-semibold text-gray-500">Intake:</span>
        <ToolTip
          title="No intake forms were included in the reminder."
          hide={summaryStatus !== 'Reminder only'}
        >
          <span className="inline-flex">
            <StatusTag
              value={summaryLabel}
              statusMap={{
                error: summaryStatus === 'Incomplete' ? summaryLabel : 'Incomplete',
                success: 'Completed',
                pending: summaryStatus === 'Partially completed'
                  ? summaryLabel
                  : 'Partially completed',
                off: summaryStatus === 'Reminder only' ? 'Reminder Only' : 'Not Sent',
              }}
              classes={{
                value: 'gap-1',
              }}
            />
          </span>
        </ToolTip>
      </div>
      {requestedCount > 0 && (
        <div
          aria-label="Intake section breakdown"
          className="mt-2 flex items-center justify-end"
          role="group"
        >
          <StatusLine
            label="HPI"
            status={intakeStatus.hpi}
          />
          <StatusLine
            label="Medical history"
            status={intakeStatus.medicalHistory}
          />
          <StatusLine
            label="Meds & Allergies"
            status={intakeStatus.pharmacyAllergyMedications}
          />
        </div>
      )}
    </div>
  );
}
